import { Component } from '@angular/core';
import * as moment from 'moment';
import { HttpService } from 'src/app/service/http.service';
import { GeneralSupplierInvoicesFilterForm } from '../../form-template/general-supplier-invoices-filter-form';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import Decimal from 'decimal.js';
import { GeneralSupplier } from 'src/app/general-supplier';
import { forkJoin } from 'rxjs';
import { GeneralSupplierUnpaidInvoice } from 'src/app/general-supplier-unpaid-invoice';
import { ToastMessageService } from 'src/app/toast-message.service';
import { MenuItem } from 'primeng/api';
import { UserService } from 'src/app/service/user.service';
import { ApiError } from 'src/app/api-error';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-unpaid-invoices',
  templateUrl: './unpaid-invoices.component.html',
  styleUrl: './unpaid-invoices.component.css'
})
export class UnpaidInvoicesComponent {

  invoices: GeneralSupplierUnpaidInvoice[] = [];
  filteredInvoices: GeneralSupplierUnpaidInvoice[] = [];
  filterForm: FormGroup<GeneralSupplierInvoicesFilterForm>;
  suppliers: GeneralSupplier[] = [];
  canAddInvoiceToBePaid = false;
  topMenuItems: MenuItem[] = [];

  constructor(
    private httpService: HttpService,
    private builder: FormBuilder,
    private toastService: ToastMessageService,
    private userService: UserService
  ) {
    if (userService.isNot("accountant")) {
      this.canAddInvoiceToBePaid = true;
    }
    this.topMenuItems = [
      { label: "Invoices", routerLink: ["/management/general-supplier-invoice/invoices"] },
      { label: "Add paid invoice", routerLink: ["/management/general-supplier-invoice/create-paid-invoice"], visible: this.canAddInvoiceToBePaid },
      { label: "Add invoice to be paid", routerLink: ["/management/general-supplier-invoice/create-unpaid-invoice"], visible: this.canAddInvoiceToBePaid }
    ];
    forkJoin([
      httpService.getGeneralSuppliers(),
      httpService.getGeneralSupplierUnpaidInvoices()
    ]).subscribe({
      next: ([suppliers, invoices]) => {
        this.invoices = invoices;
        for (let invoice of invoices) {
          this.filteredInvoices.push(invoice);
        }
        this.suppliers = suppliers;
      },
      error: (error) => {
        console.error("Error", error);
      }
    })

    this.filterForm = builder.group<GeneralSupplierInvoicesFilterForm>(
      {
        dateAt: new FormControl(),
        dateFrom: new FormControl(),
        dateTo: new FormControl(),
        minAmount: new FormControl(),
        maxAmount: new FormControl(),
        supplier: new FormControl()
      }
    );
  }

  customSort(event: any){
      if(event.field === "date"){
        event.data.sort((data1: any, data2: any) => {
          const value1 = moment(data1[event.field]);
          const value2 = moment(data2[event.field]);
  
          let result = 0;
          if(value1.isBefore(value2)){
            result = -1;
          } else if (value1.isAfter(value2)){
            result = 1;
          }
          return (event.order * result);
        })
      } else if(event.field === "supplier.name"){
        event.data.sort((data1: any, data2: any) => {
          let value1 = data1.supplier.name || "";
          let value2 = data2.supplier.name || "";
          let result = 0;
  
          value1 = value1.toLowerCase();
          value2 = value2.toLowerCase();
          result = value1.localeCompare(value2);
          return (event.order * result);
      });
      
    } else if(event.field === "invoiceNumber"){
      event.data.sort((data1: any, data2: any) => {
        let value1 = data1.invoiceNumber || "";
        let value2 = data2.invoiceNumber || "";
        let result = 0;
  
        value1 = value1.toLowerCase();
        value2 = value2.toLowerCase();
        result = value1.localeCompare(value2);
        return (event.order * result);
    });
    }
    else {
        event.data.sort((data1: any, data2: any) => {
          const value1 = data1[event.field];
          const value2 = data2[event.field];
          let result = 0;
          if(value1 < value2){
            result = -1;
          } else if(value1 > value2){
            result = 1;
          }
          return (event.order * result);
        });
      }
    }

  downloadPdfFile(invoiceId: number, fileName: string, isValidated: boolean) {
    if (isValidated) {
      this.httpService.getGeneralSupplierInvoiceFileData(invoiceId).subscribe(res => {
        let binaryData = [];
        binaryData.push(res);
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
        downloadLink.setAttribute('download', fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
      });
    } else {
      this.toastService.addError("Warning", "This invoice is not verified by administrator yet.");
    }
  }

  filterBySingleDate() {
    if (this.filterForm.value.dateAt) {
      this.filterForm.controls.dateFrom.setValue(null);
      this.filterForm.controls.dateTo.setValue(null);
    }
  }

  filterByDateRange() {
    if (this.filterForm.value.dateFrom || this.filterForm.value.dateTo) {
      this.filterForm.controls.dateAt.setValue(null);
    }
  }

  filterInvoices() {
    this.filteredInvoices = [];
    for (let invoice of this.invoices) {
      this.filteredInvoices.push(invoice);
    }
    if (this.filterForm.value.dateAt) {
      this.filteredInvoices = this.filteredInvoices.filter(invoice => {
        return moment(invoice.date).isSame(moment(this.filterForm.value.dateAt));
      });
    }
    if (this.filterForm.value.dateFrom && this.filterForm.value.dateTo) {
      this.filteredInvoices = this.filteredInvoices.filter(invoice => {
        let invoiceDate = moment(invoice.date);
        let invoiceDateFrom = moment(this.filterForm.value.dateFrom);
        let invoiceDateTo = moment(this.filterForm.value.dateTo);
        return invoiceDateFrom.isSameOrBefore(invoiceDate) && invoiceDate.isSameOrBefore(invoiceDateTo);
      });
    }
    if (this.filterForm.value.minAmount && this.isDecimal(this.filterForm.value.minAmount)) {
      this.filteredInvoices = this.filteredInvoices.filter(invoice => {
        return new Decimal(invoice.amount).greaterThanOrEqualTo(new Decimal(<number>this.filterForm.value.minAmount));
      });
    }
    if (this.filterForm.value.maxAmount && this.isDecimal(this.filterForm.value.maxAmount)) {
      this.filteredInvoices = this.filteredInvoices.filter(invoice => {
        return new Decimal(invoice.amount).lessThanOrEqualTo(new Decimal(<number>this.filterForm.value.maxAmount));
      });
    }
    if (this.filterForm.value.supplier) {
      this.filteredInvoices = this.filteredInvoices.filter(invoice => {
        return invoice.supplier.id === this.filterForm.value.supplier?.id;
      });
    }
  }

  isDecimal(val: any) {
    try {
      new Decimal(val);
      return true;
    }
    catch (error) {
      return false;
    }
  }

  clearFilters() {
    this.filterForm.reset();
    this.filteredInvoices = [];
    for (let invoice of this.invoices) {
      this.filteredInvoices.push(invoice);
    }
  }

  markAsPaid(invoice: GeneralSupplierUnpaidInvoice) {
    if (invoice.isValidated) {
      //update invoice as paid
      this.httpService.markGeneralSupplierInvoiceAsPaid(invoice.id!).subscribe(() => {
        invoice.isPaid = true;
        this.toastService.addSuccess("Success", "Payment encoded");
      }, error => {
        this.toastService.addApiError(new ApiError(error));
      });
    } else {
      this.toastService.addError("Warning", "This invoice is not verified by administrator. Don't pay this yet.");
    }
  }

  openEditWindow(invoiceId: number){
    let destinationUrl = environment.sbmsBaseUrl + "management/general-supplier-invoice/edit-unpaid-invoice/" + invoiceId;
    window.open(destinationUrl);
  }
}